import { BasePayloadMapper } from '@ps/utils';
import { ManagedUserPayloadDto } from '../dtos/managed-user.dto';
import { ManagedUserDraft } from '../../models/managed-user-draft.model';
import { isMultiBuyerManagerOption } from '../../models/access-level-option.model';

const STATUS_ACTIVE = 1;
const STATUS_INACTIVE = 2;

export class ManagedUserPayloadMapper extends BasePayloadMapper<
  ManagedUserDraft,
  ManagedUserPayloadDto
> {
  public toPayload(draft: ManagedUserDraft): ManagedUserPayloadDto {
    return {
      email: draft.email,
      name: draft.name,
      company: draft.company.id,
      companyName: draft.company.name,
      slackMemberId: draft.slackMemberId,
      apiUserStatus: draft.isApiUser,
      welcomeEmailStatus: draft.sendWelcomeEmail,
      buyerSideAccessLevels: draft.buyerAccessLevel?.value ?? 'none',
      supplierSideAccessLevels: draft.supplierAccessLevel?.value ?? 'none',
      operatorAccessLevels: draft.operatorAccessLevel?.value ?? 'none',
      questionLibraryAccessLevel: Number(draft.hasQuestionLibraryAccess),
      ...this._password(draft),
      ...this._token(draft),
      ...this._status(draft),
      ...this._projectManager(draft),
      ...this._primaryEntity(draft),
      ...this._multiBuyerManager(draft),
    };
  }

  private _password(draft: ManagedUserDraft): Partial<ManagedUserPayloadDto> {
    return draft.password ? { password: draft.password } : {};
  }

  private _token(draft: ManagedUserDraft): Partial<ManagedUserPayloadDto> {
    return draft.isApiUser && draft.token ? { token: draft.token } : {};
  }

  /** Status only travels on update, encoded as 1 = active, 2 = inactive. */
  private _status(draft: ManagedUserDraft): Partial<ManagedUserPayloadDto> {
    if (!draft.id) {
      return {};
    }

    return {
      status: draft.status === 'Active' ? STATUS_ACTIVE : STATUS_INACTIVE,
    };
  }

  private _projectManager(
    draft: ManagedUserDraft,
  ): Partial<ManagedUserPayloadDto> {
    const projectManager = draft.defaultProjectManager;
    if (!projectManager) {
      return {};
    }

    return {
      proxyProjectManager: {
        id: projectManager.id,
        nm: projectManager.name,
        email: projectManager.email,
      },
    };
  }

  private _primaryEntity(
    draft: ManagedUserDraft,
  ): Partial<ManagedUserPayloadDto> {
    return draft.primaryEntity
      ? { primary_entity_id: draft.primaryEntity.id }
      : {};
  }

  /**
   * Promoting sends `true`; demoting someone who was a manager has to send
   * `false` explicitly. Otherwise the key is omitted entirely.
   */
  private _multiBuyerManager(
    draft: ManagedUserDraft,
  ): Partial<ManagedUserPayloadDto> {
    const buyerAccessLevel = draft.buyerAccessLevel;
    if (buyerAccessLevel && isMultiBuyerManagerOption(buyerAccessLevel)) {
      return { isMBManager: true };
    }

    return draft.wasMultiBuyerManager ? { isMBManager: false } : {};
  }
}
